"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { useAuthoringScope } from "../authoring-scope";
import { DrillBreadcrumbs } from "./breadcrumbs";
import { DrillCollectionLibrary } from "./collections";
import { DrillEditor } from "./editor";
import { DrillIcon } from "./icon";
import { DrillError, DrillShell, redirectForAuth } from "./shared";
import { type DrillCollection, request } from "./types";

export function NewDrill({
  collectionId: initialCollectionId,
}: {
  collectionId?: string;
  layoutId?: string;
  draftId?: string;
}) {
  const params = useSearchParams();
  const collectionId = params.get("collection") ?? initialCollectionId;
  const layoutId = params.get("layout") ?? undefined;
  const draftId = params.get("draft") ?? undefined;
  return collectionId ? (
    <ScopedNewDrill
      key={collectionId}
      collectionId={collectionId}
      layoutId={layoutId}
      draftId={draftId}
    />
  ) : (
    <DrillCollectionLibrary picking layoutId={layoutId} />
  );
}

function ScopedNewDrill({
  collectionId,
  layoutId,
  draftId,
}: {
  collectionId: string;
  layoutId?: string;
  draftId?: string;
}) {
  const authoring = useAuthoringScope();
  const router = useRouter();
  const [collection, setCollection] = useState<DrillCollection>();
  const [error, setError] = useState("");
  const [entry, setEntry] = useState(0);
  const [notice, setNotice] = useState("");
  const load = useCallback(async () => {
    setError("");
    try {
      setCollection(
        await request<DrillCollection>(
          authoring.api(`/api/drill-collections/${collectionId}`),
        ),
      );
    } catch (error) {
      if (!redirectForAuth(error, authoring.href("/login")))
        setError((error as Error).message);
    }
  }, [collectionId, authoring]);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <DrillShell>
      {(!collection?.canEdit || collection.isPlaylist) && (
        <DrillBreadcrumbs collection={collection} current="ドリルを追加" />
      )}
      {collection && (!collection.canEdit || collection.isPlaylist) ? (
        <section className="drill-panel">
          <h1>このドリル集には追加できません</h1>
          <p>
            {collection.isPlaylist
              ? "この集には、ドリル一覧で選択した元のドリルがまとまっています。"
              : "公開されたドリル集では、提供者が作成したドリルを使って練習できます。"}
          </p>
          <a
            className="primary-button"
            href={authoring.href(`/drills/collections/${collection.id}`)}
          >
            ドリル集を開く
          </a>
          <a className="text-link" href={authoring.href("/drills/new")}>
            自分のドリル集を選ぶ
          </a>
        </section>
      ) : collection ? (
        <>
          {notice && (
            <div className="drill-editor-page">
              <p className="drill-saved-notice" role="status">
                <DrillIcon name="check" size={16} />
                {notice}
              </p>
            </div>
          )}
          <DrillEditor
            key={`${entry}:${entry === 0 ? (draftId ?? "") : ""}:${entry === 0 ? (layoutId ?? "") : ""}`}
            collection={collection}
            initialLayoutId={entry === 0 ? layoutId : undefined}
            draftId={entry === 0 ? draftId : undefined}
            focusTitle={entry > 0}
            onSaved={() =>
              router.replace(
                authoring.href(`/drills/collections/${collection.id}`),
              )
            }
            onSavedAndContinue={(drill) => {
              setNotice(
                `「${drill.title}」を追加しました。次のドリルを入力できます。`,
              );
              setEntry((entry) => entry + 1);
              router.replace(
                authoring.href(
                  `/drills/new?collection=${encodeURIComponent(collection.id)}`,
                ),
                { scroll: false },
              );
              window.scrollTo({ top: 0 });
            }}
            onCancel={() =>
              router.push(
                authoring.href(`/drills/collections/${collection.id}`),
              )
            }
          />
        </>
      ) : error ? (
        <>
          <DrillError message={error} />
          <div className="drill-error-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={() => void load()}
            >
              再読み込み
            </button>
            <a
              className="text-link"
              href={authoring.href(
                `/drills/new${layoutId ? `?layout=${encodeURIComponent(layoutId)}` : ""}`,
              )}
            >
              ドリル集を選び直す
            </a>
          </div>
        </>
      ) : (
        <p className="drill-loading" role="status">
          追加先のドリル集を読み込み中…
        </p>
      )}
    </DrillShell>
  );
}
